import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { UiAtomButtonComponent } from '../../atoms/ui-atom-button/ui-atom-button.component';
import { UiMoleculeInputComponent } from '../ui-molecule-input/ui-molecule-input.component';
import { UiAtomTextComponent } from '../../atoms/ui-atom-text/ui-atom-text.component';
import { TranslatePipe } from '@ngx-translate/core';
// SurveyJS imports - ready for integration
// TODO: Use SurveyCreatorModule for form DESIGN (admins create form schemas)
// TODO: Use SurveyModule for form RESPONSE (users fill out forms)
import { SurveyCreatorModule } from 'survey-creator-angular';
import { SurveyModule } from 'survey-angular-ui';
import { SurveyCreatorModel } from 'survey-creator-core';
import 'survey-core/survey-core.min.css';
import 'survey-creator-core/survey-creator-core.min.css';

export interface FormUpsertData {
  title: string;
  message?: string;
  defaults?: {
    name?: string;
  };
  mode?: 'create' | 'edit';
  form?: any;
  context?: 'global' | 'country' | 'partner';
  countryId?: string;
  partnerId?: string;
}

export interface FormDialogResult {
  mode: 'create' | 'edit';
  payload: any;
  formId?: string;
  context?: 'global' | 'country' | 'partner';
  countryId?: string;
  partnerId?: string;
}

// Survey Creator options for form design
const creatorOptions = {
  showLogicTab: true,
  showTranslationTab: true,
};

@Component({
  selector: 'ui-molecule-dialog-form-upsert',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    UiMoleculeInputComponent,
    UiAtomButtonComponent,
    UiAtomTextComponent,
    MatDialogClose,
    TranslatePipe,
    SurveyCreatorModule,
  ],
  templateUrl: './ui-molecule-dialog-form-upsert.component.html',
  styleUrl: './ui-molecule-dialog-form-upsert.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class FormUpsertDialogComponent implements AfterViewInit, OnDestroy {
  form: FormGroup;
  isLoading = false;
  errorMessage = '';
  isExpanded = false;

  @ViewChild('nameInput') nameInput?: UiMoleculeInputComponent;

  // Survey Creator instance for designing forms
  surveyCreator!: SurveyCreatorModel;

  readonly nameMax = 50;

  get nameErrorKey(): string | null {
    const ctrl = this.form.get('name');
    if (!ctrl || (!ctrl.touched && !ctrl.dirty)) return null;
    if (ctrl.hasError('required')) return 'forms.form.name.error.required';
    if (ctrl.hasError('maxlength')) return 'forms.form.name.error.maxLength';
    return null;
  }

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    public dialogRef: MatDialogRef<FormUpsertDialogComponent, FormDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: FormUpsertData,
  ) {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(this.nameMax)]],
    });
  }

  ngAfterViewInit(): void {
    // Cargar datos por defecto o del formulario a editar
    if (this.data.defaults) {
      this.form.patchValue(this.data.defaults);
    }

    if (this.data.mode === 'edit' && this.data.form) {
      this.form.patchValue({
        name: this.data.form.name,
      });
    }

    // Initialize Survey Creator for form design
    this.surveyCreator = new SurveyCreatorModel(creatorOptions);

    // If editing, load existing schema
    if (this.data.mode === 'edit' && this.data.form?.schema) {
      const hasContent = Object.keys(this.data.form.schema).length > 0;
      if (hasContent) {
        this.surveyCreator.JSON = this.data.form.schema;
      }
    }

    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  /**
   * Gets the current form schema from Survey Creator
   */
  getFormSchema(): Record<string, unknown> {
    return this.surveyCreator?.JSON || {};
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.nameInput?.focusInput();
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    const formData = this.form.getRawValue();

    if (this.data.mode === 'edit' && this.data.form) {
      // Modo edición - PUT /forms/{id} solo acepta name/schema (additionalProperties: false).
      // countryId/partnerId no son editables, solo válidos en la creación (POST /forms).
      const payload: any = {
        name: formData.name,
        schema: this.getFormSchema(),
      };

      this.dialogRef.close({
        mode: 'edit',
        payload: payload,
        formId: this.data.form.id,
        context: this.data.context,
        countryId: this.data.countryId,
        partnerId: this.data.partnerId,
      });
    } else {
      // Modo creación - include the schema from Survey Creator
      const payload: any = {
        name: formData.name,
        schema: this.getFormSchema(),
      };

      // Incluir countryId o partnerId en el payload si están presentes
      if (this.data.countryId) {
        payload.countryId = this.data.countryId;
      }
      if (this.data.partnerId) {
        payload.partnerId = this.data.partnerId;
      }

      this.dialogRef.close({
        mode: 'create',
        payload: payload,
        context: this.data.context,
        countryId: this.data.countryId,
        partnerId: this.data.partnerId,
      });
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;

    if (this.isExpanded) {
      this.dialogRef.updateSize('100vw', '100vh');
      this.dialogRef.addPanelClass('dialog-expanded');
      this.dialogRef.removePanelClass('dialog-safe-area');
    } else {
      this.dialogRef.updateSize('95vw', '90vh');
      this.dialogRef.removePanelClass('dialog-expanded');
      this.dialogRef.addPanelClass('dialog-safe-area');
    }
  }
}
